import React from 'react';
import { RotateCcw, Clock, CheckCircle, XCircle, Package, CreditCard, Phone, Mail, HelpCircle, Gift, Shield, Truck, Crown } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

const ReturnPolicy = () => {
  const { settings } = useSiteSettings();
  
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Section */}
      <div className="bg-[#0A0A0A] text-white py-20 border-b border-[#D4AF37]/20">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#D4AF37]/50">
            <RotateCcw className="h-10 w-10 text-[#D4AF37]" />
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold mb-4 font-heading">Returns & Exchanges</h1>
          <p className="text-xl text-[#888888] font-body">Your complete satisfaction is our commitment</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16 max-w-4xl">
        {/* Satisfaction Guarantee Banner */}
        <div className="bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37] text-[#0A0A0A] p-8 rounded-2xl mb-12 shadow-xl shadow-[#D4AF37]/20">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-[#0A0A0A] flex items-center justify-center flex-shrink-0">
              <Shield className="h-12 w-12 text-[#D4AF37]" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-5 w-5" />
                <span className="font-bold uppercase tracking-wide text-sm">BOXVERRA Promise</span>
              </div>
              <h2 className="text-3xl font-bold font-heading mb-2">30-Day Satisfaction Guarantee</h2>
              <p className="text-[#0A0A0A]/80 font-body text-lg">
                We stand behind every piece. If you're not completely satisfied, we'll make it right.
              </p>
            </div>
          </div>
        </div>

        {/* Return Windows */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30">
              <Clock className="h-6 w-6 text-[#D4AF37]" />
            </div>
            Return Timeframes
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#141414] p-8 rounded-2xl border-2 border-[#D4AF37] shadow-lg shadow-[#D4AF37]/20 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0A0A0A] px-4 py-1 rounded-full text-sm font-bold">
                STANDARD
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#D4AF37] mb-2">30</div>
                <div className="text-[#888888] font-body text-lg mb-4">Days</div>
                <p className="text-[#888888] font-body text-sm">
                  Full refund or exchange for all regular items in original condition
                </p>
              </div>
            </div>

            <div className="bg-[#141414] p-8 rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#D4AF37] mb-2">14</div>
                <div className="text-[#888888] font-body text-lg mb-4">Days</div>
                <p className="text-[#888888] font-body text-sm">
                  Sale items — store credit only
                </p>
              </div>
            </div>

            <div className="bg-[#141414] p-8 rounded-2xl border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 transition-colors">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#D4AF37] mb-2">90</div>
                <div className="text-[#888888] font-body text-lg mb-4">Days</div>
                <p className="text-[#888888] font-body text-sm">
                  Extended window for VIP members
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 p-6 bg-[#141414] rounded-xl border border-[#D4AF37]/20">
            <div className="flex items-start gap-3">
              <Gift className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <p className="text-[#888888] font-body">
                <strong className="text-white">Holiday Extension:</strong> Purchases made November 15 - December 31 can be returned until January 31.
              </p>
            </div>
          </div>
        </section>

        {/* Eligibility */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30">
              <CheckCircle className="h-6 w-6 text-[#D4AF37]" />
            </div>
            Return Eligibility
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#141414] rounded-2xl border border-[#D4AF37]/20 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-bold text-[#D4AF37] text-xl font-heading">Accepted Returns</h3>
              </div>
              <ul className="space-y-4 text-[#888888] font-body">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Items in original, unopened condition</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Products with original tags and packaging</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Items with proof of purchase</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span>Defective or damaged upon arrival</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#141414] rounded-2xl border border-[#D4AF37]/20 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-400" />
                </div>
                <h3 className="font-bold text-red-400 text-xl font-heading">Non-Returnable</h3>
              </div>
              <ul className="space-y-4 text-[#888888] font-body">
                <li className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span>Opened personal care items</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span>Used candles or aromatherapy products</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span>Custom or personalized pieces</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span>Items marked "Final Sale"</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* How to Return */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30">
              <Package className="h-6 w-6 text-[#D4AF37]" />
            </div>
            Return Process
          </h2>

          <div className="bg-[#141414] rounded-2xl border border-[#D4AF37]/20 p-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h3 className="font-bold text-white font-heading mb-2">Initiate Request</h3>
                <p className="text-[#888888] font-body text-sm">
                  Contact our concierge team via email or phone
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h3 className="font-bold text-white font-heading mb-2">Receive Label</h3>
                <p className="text-[#888888] font-body text-sm">
                  Prepaid shipping label sent within 24 hours
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">3</span>
                </div>
                <h3 className="font-bold text-white font-heading mb-2">Ship Item</h3>
                <p className="text-[#888888] font-body text-sm">
                  Pack securely and drop off at any carrier location
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold">4</span>
                </div>
                <h3 className="font-bold text-white font-heading mb-2">Get Refund</h3>
                <p className="text-[#888888] font-body text-sm">
                  Processed within 5-7 business days
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Refund Information */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border border-[#D4AF37]/30">
              <CreditCard className="h-6 w-6 text-[#D4AF37]" />
            </div>
            Refund Details
          </h2>

          <div className="bg-[#141414] rounded-2xl border border-[#D4AF37]/20 overflow-hidden">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#D4AF37]/20">
              <div className="p-8">
                <h3 className="font-bold text-white text-xl mb-6 font-heading">Refund Options</h3>
                <ul className="space-y-4 text-[#888888] font-body">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Original Payment:</strong> Automatic credit to original method
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Store Credit:</strong> Instant processing + 10% bonus
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Exchange:</strong> Swap for different item
                    </div>
                  </li>
                </ul>
              </div>
              <div className="p-8">
                <h3 className="font-bold text-white text-xl mb-6 font-heading">Processing Timeline</h3>
                <ul className="space-y-4 text-[#888888] font-body">
                  <li className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Inspection:</strong> 1-2 business days
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Processing:</strong> 3-5 business days
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-white">Store Credit:</strong> Immediate
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37] text-[#0A0A0A] p-8 rounded-2xl shadow-xl shadow-[#D4AF37]/20">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold font-heading mb-2">Need Assistance?</h2>
            <p className="text-[#0A0A0A]/80 font-body">Our concierge team is here for you</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <a href={`mailto:${settings?.email || 'support@boxverra.com'}`} className="flex flex-col items-center gap-3 bg-[#0A0A0A] text-[#D4AF37] p-6 rounded-xl transition-colors hover:bg-[#1A1A1A]">
              <Mail className="h-8 w-8" />
              <span className="font-body">{settings?.email || 'support@boxverra.com'}</span>
            </a>
            <a href={`tel:${settings?.phone?.replace(/\D/g, '') || '18001234567'}`} className="flex flex-col items-center gap-3 bg-[#0A0A0A] text-[#D4AF37] p-6 rounded-xl transition-colors hover:bg-[#1A1A1A]">
              <Phone className="h-8 w-8" />
              <span className="font-body">{settings?.phone || '+1 (800) 123-4567'}</span>
            </a>
            <div className="flex flex-col items-center gap-3 bg-[#0A0A0A] text-[#D4AF37] p-6 rounded-xl">
              <Clock className="h-8 w-8" />
              <span className="font-body text-center">Mon-Fri: 9AM-6PM EST</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ReturnPolicy;
